import React, { useState, useRef } from 'react';
import api from '../lib/api';
import { useI18n } from '../context/I18nContext';
import { toast } from 'sonner';
import { Loader2, Upload, X, Image as ImageIcon } from 'lucide-react';

const MAX_FILES = 8;
const MAX_SIZE = 5 * 1024 * 1024; // 5 MB
const ACCEPT = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

const readAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function SellWatch() {
  const { t } = useI18n();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', brand: '', model: '', year: '', condition: 'good',
    price_expectation: '', description: '', has_box: false, has_papers: false, images: [],
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const slots = MAX_FILES - form.images.length;
    if (slots <= 0) { toast.error(`Max ${MAX_FILES}`); return; }
    const taken = files.slice(0, slots);
    const accepted = taken.filter((f) => ACCEPT.includes(f.type) && f.size <= MAX_SIZE);
    if (accepted.length < taken.length) toast.message('Alcuni file scartati (formato o > 5 MB)');
    const dataUrls = await Promise.all(accepted.map(readAsDataURL));
    setForm((f) => ({ ...f, images: [...f.images, ...dataUrls] }));
  };

  const removeImage = (idx) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        year: form.year ? parseInt(form.year, 10) : null,
        price_expectation: form.price_expectation ? parseFloat(form.price_expectation) : null,
      };
      await api.post('/submissions', payload);
      toast.success(t('sell.success'));
      setForm({ name: '', email: '', phone: '', brand: '', model: '', year: '', condition: 'good',
        price_expectation: '', description: '', has_box: false, has_papers: false, images: [] });
    } catch { toast.error(t('sell.error')); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-16 lg:py-24" data-testid="sell-page">
      <p className="gn-overline mb-4">{t('nav.sell')}</p>
      <h1 className="font-serif text-5xl md:text-6xl text-[#f5f5f0] mb-6">{t('sell.title')}</h1>
      <p className="text-lg text-[#a3b5ad] font-light max-w-2xl mb-16">{t('sell.subtitle')}</p>

      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8" data-testid="sell-form">
        <div>
          <label className="gn-overline mb-3 block">{t('sell.name')} *</label>
          <input required className="gn-input" value={form.name} onChange={(e) => update('name', e.target.value)} data-testid="sell-name" />
        </div>
        <div>
          <label className="gn-overline mb-3 block">{t('sell.email')} *</label>
          <input required type="email" className="gn-input" value={form.email} onChange={(e) => update('email', e.target.value)} data-testid="sell-email" />
        </div>
        <div>
          <label className="gn-overline mb-3 block">{t('sell.phone')}</label>
          <input className="gn-input" value={form.phone} onChange={(e) => update('phone', e.target.value)} data-testid="sell-phone" />
        </div>
        <div>
          <label className="gn-overline mb-3 block">{t('sell.brand')} *</label>
          <input required className="gn-input" value={form.brand} onChange={(e) => update('brand', e.target.value)} data-testid="sell-brand" />
        </div>
        <div>
          <label className="gn-overline mb-3 block">{t('sell.model')} *</label>
          <input required className="gn-input" value={form.model} onChange={(e) => update('model', e.target.value)} data-testid="sell-model" />
        </div>
        <div>
          <label className="gn-overline mb-3 block">{t('sell.year')}</label>
          <input type="number" className="gn-input" value={form.year} onChange={(e) => update('year', e.target.value)} data-testid="sell-year" />
        </div>
        <div>
          <label className="gn-overline mb-3 block">{t('sell.condition')}</label>
          <select className="gn-input bg-[#05150f]" value={form.condition} onChange={(e) => update('condition', e.target.value)} data-testid="sell-condition">
            <option value="unworn">{t('catalog.condition_unworn')}</option>
            <option value="excellent">{t('catalog.condition_excellent')}</option>
            <option value="good">{t('catalog.condition_good')}</option>
          </select>
        </div>
        <div>
          <label className="gn-overline mb-3 block">{t('sell.price')}</label>
          <input type="number" step="100" className="gn-input" value={form.price_expectation} onChange={(e) => update('price_expectation', e.target.value)} data-testid="sell-price" />
        </div>

        {/* PHOTO UPLOAD BOX */}
        <div className="md:col-span-2">
          <label className="gn-overline mb-3 block">{t('sell.photos')}</label>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
            onClick={() => inputRef.current?.click()}
            className={`border-2 border-dashed cursor-pointer transition-colors p-10 text-center ${dragOver ? 'border-[#c9a961] bg-[#0d2a1f]/40' : 'border-[#c9a961]/30 hover:border-[#c9a961]/60'}`}
            data-testid="sell-dropzone"
          >
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT.join(',')}
              multiple
              className="hidden"
              onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
              data-testid="sell-file-input"
            />
            <Upload size={32} className="mx-auto text-[#c9a961] mb-4" strokeWidth={1.2} />
            <p className="font-serif text-lg text-[#f5f5f0] mb-2">{t('sell.upload_t')}</p>
            <p className="text-sm text-[#a3b5ad] font-light">{t('sell.upload_b')}</p>
            <p className="text-xs text-[#a3b5ad]/70 mt-3 tracking-wider">{form.images.length} / {MAX_FILES}</p>
          </div>

          {form.images.length > 0 && (
            <div className="mt-6 grid grid-cols-4 md:grid-cols-6 gap-3" data-testid="sell-photo-grid">
              {form.images.map((src, i) => (
                <div key={i} className="relative aspect-square overflow-hidden border border-[#c9a961]/20 group">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removeImage(i); }}
                    className="absolute top-1 right-1 bg-[#05150f]/90 border border-[#c9a961]/40 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`sell-photo-remove-${i}`}
                    aria-label="Remove"
                  >
                    <X size={12} className="text-[#c9a961]" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="gn-overline mb-3 block">{t('sell.description')}</label>
          <textarea className="gn-textarea" value={form.description} onChange={(e) => update('description', e.target.value)} data-testid="sell-description" />
        </div>
        <div className="flex items-center gap-3">
          <input type="checkbox" id="box" checked={form.has_box} onChange={(e) => update('has_box', e.target.checked)} className="accent-[#c9a961] w-4 h-4" data-testid="sell-has-box" />
          <label htmlFor="box" className="text-sm text-[#f5f5f0] cursor-pointer">{t('sell.has_box')}</label>
        </div>
        <div className="flex items-center gap-3">
          <input type="checkbox" id="papers" checked={form.has_papers} onChange={(e) => update('has_papers', e.target.checked)} className="accent-[#c9a961] w-4 h-4" data-testid="sell-has-papers" />
          <label htmlFor="papers" className="text-sm text-[#f5f5f0] cursor-pointer">{t('sell.has_papers')}</label>
        </div>
        <div className="md:col-span-2 pt-6">
          <button type="submit" disabled={submitting} className="gn-btn-primary" data-testid="sell-submit">
            {submitting && <Loader2 className="animate-spin" size={16} />}
            {t('sell.submit')}
          </button>
        </div>
      </form>
    </div>
  );
}
